/* eslint-disable react/require-default-props */
import {
  forwardRef, useMemo, useState, ReactNode,
} from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useAuth } from "./useAuth";
import {
  Modal, Select, Dropdown, Button, Toggle,
} from "@xfrontend/xui-react-plus";
import { MoreFunctions } from "@xfrontend/xicon";
import { toast } from "#/infra/Toastify";
import { LicenseItem } from "#/apis/infra/listLicenses";
import { signOutClean } from "./idp";

type SortKey = "expiry" | "name";

type OrgSwitchModalProps = {
  show: boolean;
  forced: boolean;
  orgs: LicenseItem[];
  activeOrgId: string | null;
  defaultOrgId: string | null;
  onSwitch: (id: string) => void;
  onSetDefault: (id: string) => Promise<boolean>;
  // When provided (caller has manage permission), shows a "Manage Organization" card action.
  onManageOrg?: (licenseId: string) => void;
  // When provided (caller can edit orgs — system admin), shows an "Edit Organization" card action.
  onEditOrg?: (licenseId: string) => void;
  onClose: () => void;
};

const StyledModal = styled(Modal)`
  .modal-dialog {
    width: 960px;
    max-width: calc(100% - 32px);
  }
  .modal-content {
    background: var(--x-bg-500, #2d293a);
    border: 1px solid var(--x-purple-500, #494358);
  }
`;

const Subtitle = styled.p`
  font-size: 13px;
  color: var(--x-gray-400, #cfd0d3);
  margin: 0 0 16px;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 16px;
  .search { flex: 1; min-width: 240px; }
`;

const ToggleLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--x-gray-400, #cfd0d3);
  font-size: 12.5px;
  cursor: pointer;
  white-space: nowrap;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  @media (max-width: 820px) { grid-template-columns: repeat(2, 1fr); }
`;

type CardSc = { $current: boolean; $disabled: boolean };
// Light card on the dark modal — matches xcockpit's CustomerCard (#5C5773).
const Card = styled.div<CardSc>`
  position: relative;
  background: #5c5773;
  border: 2px solid ${(p: CardSc) => (p.$current ? "var(--x-gray-200, #f6f6f7)" : "transparent")};
  border-radius: 10px;
  padding: 14px 15px 13px;
  cursor: ${(p: CardSc) => (p.$disabled ? "not-allowed" : "pointer")};
  opacity: ${(p: CardSc) => (p.$disabled ? 0.5 : 1)};
  transition: border-color 0.12s;
  &:hover { border-color: ${(p: CardSc) => (p.$disabled ? "transparent" : "rgba(255, 255, 255, 0.5)")}; }
`;

const CardHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;
`;

const OrgName = styled.div`
  font-size: 15px;
  font-weight: 650;
  color: var(--x-gray-200, #f6f6f7);
  line-height: 1.25;
`;

const RoleBadge = styled.span`
  font-size: 10px;
  font-weight: 700;
  color: var(--x-gray-300, #e9e9eb);
  background: var(--x-purple-500, #494358);
  border-radius: 20px;
  padding: 3px 8px;
  white-space: nowrap;
`;

// Role badge + overflow menu share one right-aligned group so they never overlap.
const HeadRight = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

// "Default" marker — a labelled tag (clearer than a bare star icon).
const DefaultTag = styled.span`
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  color: var(--x-gray-200, #f6f6f7);
  background: var(--x-purple, #846ce6);
  border-radius: 20px;
  padding: 3px 8px;
  white-space: nowrap;
`;

const Territory = styled.div`
  font-size: 12.5px;
  color: var(--x-gray-400, #cfd0d3);
  margin-top: 3px;
`;

const CardFoot = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 16px;
  padding-top: 11px;
  border-top: 1px solid rgba(0, 0, 0, 0.18);
  font-size: 12px;
`;

type StatusSc = { $on: boolean };
const Status = styled.span<StatusSc>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  color: ${(p: StatusSc) => (p.$on ? "var(--x-theme-success, #14B8A6)" : "var(--x-gray-500, #a9a9ac)")};
  .dot {
    width: 8px; height: 8px; border-radius: 50%;
    background: ${(p: StatusSc) => (p.$on ? "var(--x-theme-success, #14B8A6)" : "var(--x-gray-500, #a9a9ac)")};
  }
`;

const Expiry = styled.span<{ $bad: boolean }>`
  color: ${(p: { $bad: boolean }) => (p.$bad ? "var(--x-red, #ff595a)" : "var(--x-gray-400, #cfd0d3)")};
`;

const MoreToggleBtn = styled.button`
  background: none;
  border: none;
  padding: 0 2px;
  line-height: 1;
  cursor: pointer;
  display: inline-flex;
`;

// forwardRef wrapper so XUI's Dropdown can inject its menu-open onClick (which we must CALL,
// not overwrite) while we also stop the click from bubbling to the card's switch handler.
// Mirrors xcockpit's IconToggle pattern.
type MoreToggleProps = { onClick?: (e: React.MouseEvent) => void; children?: ReactNode };

const MoreToggle = forwardRef<HTMLButtonElement, MoreToggleProps>(
  ({ onClick, children }, ref) => (
    <MoreToggleBtn
      ref={ref}
      type="button"
      onClick={(e: React.MouseEvent) => {
        e.stopPropagation();
        onClick?.(e);
      }}
    >
      {children}
    </MoreToggleBtn>
  ),
);
MoreToggle.displayName = "MoreToggle";

const FootBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  font-size: 12px;
  color: var(--x-gray-500, #a9a9ac);
`;

const isExpired = (iso: string): boolean => {
  if (!iso) return false;
  const t = Date.parse(iso);
  return Number.isFinite(t) && t < Date.now();
};

const fmtDate = (iso: string): string => (iso ? iso.slice(0, 10) : "—");

const OrgSwitchModal = ({
  show, forced, orgs, activeOrgId, defaultOrgId, onSwitch, onSetDefault, onManageOrg, onEditOrg, onClose,
}: OrgSwitchModalProps): JSX.Element => {
  const { t } = useTranslation("org");
  const auth = useAuth();
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<SortKey>("expiry");
  const [inServiceOnly, setInServiceOnly] = useState(false);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = orgs.filter((o) => {
      if (inServiceOnly && o.license_status !== "enable") return false;
      return o.license_name.toLowerCase().includes(q) || o.customer_name.toLowerCase().includes(q);
    });
    const enabledRank = (o: LicenseItem): number => (o.license_status === "enable" ? 0 : 1);
    return [...filtered].sort((a, b) => {
      // Enabled orgs always come first; within each group, sort by the selected key.
      const byStatus = enabledRank(a) - enabledRank(b);
      if (byStatus !== 0) return byStatus;
      return sort === "name"
        ? a.license_name.localeCompare(b.license_name)
        : Date.parse(a.expires_at || "") - Date.parse(b.expires_at || "");
    });
  }, [orgs, query, sort, inServiceOnly]);

  const handleSetDefault = (org: LicenseItem): void => {
    onSetDefault(org.license_id).then((ok) => {
      if (ok) toast.success(t("toast.defaultSet", { name: org.license_name }));
      else toast.error(t("toast.defaultFailed"));
    });
  };

  const handleCopy = (org: LicenseItem): void => {
    // Copy the full license object (all fields) as pretty JSON.
    navigator.clipboard.writeText(JSON.stringify(org, null, 2));
    toast.copied(t("toast.copied"));
  };

  return (
    <StyledModal
      title={t("switch.title")}
      show={show}
      onClose={forced ? () => undefined : onClose}
      showCloseBtn={!forced}
      restoreFocus={false}
    >
      <Modal.Body>
        <Subtitle>{t("switch.subtitle")}</Subtitle>
        <Toolbar>
          <div className="search">
            <Select
              name="org-search"
              placeholder={t("switch.search")}
              withSearchIndicator
              options={[]}
              inputValue={query}
              onInputChange={(val: string) => setQuery(val)}
              menuIsOpen={false}
              isClearable={false}
            />
          </div>
          <ToggleLabel>
            <Toggle
              name="in-service-only"
              checked={inServiceOnly}
              onChange={() => setInServiceOnly((v) => !v)}
            />
            {t("switch.inServiceOnly")}
          </ToggleLabel>
          <Dropdown align="end" className="no-caret">
            <Dropdown.Toggle variant="secondary" size="sm">
              {t(`sort.${sort}`)}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => setSort("expiry")}>{t("sort.expiry")}</Dropdown.Item>
              <Dropdown.Item onClick={() => setSort("name")}>{t("sort.name")}</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </Toolbar>

        <Grid>
          {visible.map((org) => {
            const enabled = org.license_status === "enable";
            const isCurrent = org.license_id === activeOrgId;
            const isDefault = org.license_id === defaultOrgId;
            const expired = isExpired(org.expires_at);
            return (
              <Card
                key={org.license_id}
                $current={isCurrent}
                $disabled={!enabled}
                onClick={() => { if (enabled) onSwitch(org.license_id); }}
              >
                <CardHead>
                  <OrgName>{org.license_name}</OrgName>
                  <HeadRight>
                    <RoleBadge>{org.effective_role}</RoleBadge>
                    <Dropdown align="end" className="no-caret">
                      <Dropdown.Toggle as={MoreToggle}>
                        <MoreFunctions size="18" color="var(--x-gray-400, #cfd0d3)" />
                      </Dropdown.Toggle>
                      <Dropdown.Menu popperConfig={{ strategy: "fixed" }}>
                        {onManageOrg && (
                          <Dropdown.Item onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            onManageOrg(org.license_id);
                          }}
                          >
                            {t("card.manage")}
                          </Dropdown.Item>
                        )}
                        {onEditOrg && (
                          <Dropdown.Item onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            onEditOrg(org.license_id);
                          }}
                          >
                            {t("card.edit")}
                          </Dropdown.Item>
                        )}
                        {!isDefault && (
                          <Dropdown.Item onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            handleSetDefault(org);
                          }}
                          >
                            {t("card.setDefault")}
                          </Dropdown.Item>
                        )}
                        <Dropdown.Item onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          handleCopy(org);
                        }}
                        >
                          {t("card.copy")}
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </HeadRight>
                </CardHead>
                <Territory>{org.customer_name}</Territory>
                <CardFoot>
                  <Status $on={enabled}>
                    <i className="dot" />
                    {enabled ? t("status.enabled") : t("status.disabled")}
                    {isDefault && <DefaultTag>{t("badge.default")}</DefaultTag>}
                  </Status>
                  <Expiry $bad={expired}>
                    {expired ? t("card.expired", { date: fmtDate(org.expires_at) })
                      : t("card.expires", { date: fmtDate(org.expires_at) })}
                  </Expiry>
                </CardFoot>
              </Card>
            );
          })}
        </Grid>
      </Modal.Body>
      <Modal.Footer>
        <FootBar>
          <span>{t("switch.hint")}</span>
          {forced
            ? (
              <Button variant="secondary" onClick={() => signOutClean(() => auth.removeUser())}>
                {t("actions.signOut")}
              </Button>
            )
            : (
              <Button variant="secondary" onClick={onClose}>
                {t("actions.close")}
              </Button>
            )}
        </FootBar>
      </Modal.Footer>
    </StyledModal>
  );
};

export default OrgSwitchModal;
